import { useEffect, Suspense, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowLeft } from "lucide-react"
import { Link } from "react-router-dom"
import { useBookingStore } from "@/store/booking-store"
import { isBranchOpen } from "@/lib/branch-hours"
import { useServerTime } from "@/utils/server-time"

import AnnouncementSection from "@/components/announcement-section"
import BookingSection from "@/components/booking-section"
import BranchGridContainer from "@/components/branch-grid-container"
import OrganizationSkeleton from "@/components/organization-skeleton"
import ServicesGridContainer from "@/components/services-grid-container"
import SchedulePage from "@/app/booking/schedule/schedule-page"
import BookingModeSelection from "@/components/booking-mode-selection"
import UserModal from "@/components/user-modal"
import BookingSummaryModal from "@/components/booking-summary-modal"
import { useAuth, User } from "@/components/auth-provider"
import ProgressPill from "@/components/progress-pill"

function useDerivedWizardStep() {
  const {
    selectedBranch,
    selectedService,
    selectedDate,
    selectedSlot,
    bookingStep,
    setBookingState,
  } = useBookingStore()

  // If user refreshes mid-flow, derive a reasonable step.
  useEffect(() => {
    let next: typeof bookingStep = "branch"
    if (selectedBranch && !selectedService) next = "service"
    if (selectedBranch && selectedService && !selectedSlot) next = "schedule"
    if (selectedBranch && selectedService && selectedSlot) next = "mode"

    if (next !== bookingStep) {
      setBookingState({ bookingStep: next })
    }
  }, [bookingStep, selectedBranch, selectedService, selectedSlot, setBookingState, selectedDate])
}

function BranchStep() {
  const { t } = useTranslation()
  const { mode } = useBookingStore()
  const backLink = mode === "reschedule" ? "/booking/history" : "/"

  return (
    <div className="md:pb-20 pb-10">
      <AnnouncementSection />
      <BookingSection />
      <BranchGridContainer />
      <div className="flex justify-start mt-1 md:mt-8 px-3">
        <Link
          to={backLink}
          className="group px-8 h-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white text-gray-900 text-sm font-semibold hover:border-pink-500 hover:text-pink-600 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </Link>
      </div>
    </div>
  )
}

function ServiceStep() {
  const { t } = useTranslation()
  const { serverDate, hasServerTime, isLoading: isServerTimeLoading, isRetrying } = useServerTime()
  const { selectedBranch, setBookingState, bookingStep } = useBookingStore()

  const branchName = selectedBranch?.name || ""
  const branchSubtitle = selectedBranch?.subtitle || ""

  const isOpen = useMemo(() => {
    if (!branchName) return false
    return isBranchOpen(branchName, serverDate)
  }, [branchName, serverDate])

  if (!selectedBranch) return null

  if (!hasServerTime && (isServerTimeLoading || isRetrying)) {
    return (
      <div className="w-full mx-auto md:px-4 md:pb-20 pt-8 font-sans">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 text-center shadow-sm">
          <p className="text-sm text-gray-500">{t("Syncing server time...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full mx-auto md:px-4 md:pb-20 pt-2 font-sans">
      <div className="hidden md:block mb-6">
        <ProgressPill value={40} />
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white py-4 md:p-5 mb-3 md:mb-6 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full blur-3xl -mr-10 -mt-10 transition-all duration-500 group-hover:bg-pink-100" />

        <div className="relative z-10 flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-4">
            <div className="md:w-12 md:h-12 md:rounded-full md:bg-pink-50 flex items-center justify-center mb-1">
              <img src="/icon/branch.svg" alt="branch" className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                {t(branchName) || t("Selected Branch")}
              </h1>
            </div>
          </div>

          {branchSubtitle && <p className="text-sm text-gray-500 md:mt-1 font-medium">{t(branchSubtitle)}</p>}

          <div className="flex flex-wrap justify-center items-center gap-3 text-xs mt-2 bg-gray-50 py-2 px-4 rounded-full border border-gray-100">
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
              <span className={`${isOpen ? "text-green-600" : "text-red-600"} font-bold`}>
                {isOpen ? t("Open") : t("Closed")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white shadow-sm border border-gray-100 p-2 md:p-6 relative overflow-hidden min-h-[400px]">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
        <div className="flex items-center justify-between mb-6 relative z-10">
          <h2 className="text-lg font-bold text-gray-900">{t("selectService")}</h2>
        </div>

        <ServicesGridContainer
          branchId={selectedBranch.id}
          branchName={branchName}
          branchSubtitle={branchSubtitle}
        />
      </div>

      <div className="flex justify-start mt-8">
        <button
          onClick={() => setBookingState({ bookingStep: "branch", selectedBranch: null })}
          className="group px-8 h-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white text-gray-900 text-sm font-semibold hover:border-pink-500 hover:text-pink-600 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </button>
      </div>
    </div>
  )
}

function ModeStep() {
  const { t } = useTranslation()
  const { serverDate, hasServerTime, isLoading: isServerTimeLoading, isRetrying } = useServerTime()
  const { user } = useAuth()

  const {
    selectedBranch,
    selectedService,
    selectedDate,
    selectedSlot,
    bookingType,
    mode,
    appointmentId,
    setBookingState,
  } = useBookingStore()

  const [userModalOpen, setUserModalOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [localAppointerUser, setLocalAppointerUser] = useState<User>({
    id: "",
    mobile: "",
    firstname: "",
    lastname: "",
    email: "",
    active: false,
    created_at: serverDate,
    updated_at: serverDate,
  })

  if (!selectedBranch || !selectedService || !selectedDate || !selectedSlot) return null

  if (!hasServerTime && (isServerTimeLoading || isRetrying)) {
    return (
      <div className="w-full mx-auto md:px-4 md:pb-24 pt-8 font-sans">
        <div className="rounded-3xl border border-gray-100 bg-white p-6 text-center shadow-sm">
          <p className="text-sm text-gray-500">{t("Syncing server time...")}</p>
        </div>
      </div>
    )
  }

  const booking = {
    branch_id: selectedBranch.id,
    branch_name: selectedBranch.name,
    service_name: selectedService.name,
    service_id: selectedService.id,
    date: selectedDate,
    time_from: selectedSlot?.time_from,
    time_to: selectedSlot?.time_to,
    duration_id: selectedSlot?.duration_id,
  }

  function proceedToSummary(selectedType: "self" | "other") {
    if (selectedType === "self") {
      if (user) setLocalAppointerUser(user)
      setSummaryOpen(true)
    } else {
      setUserModalOpen(true)
    }
  }

  return (
    <div className="w-full mx-auto md:px-4 md:pb-24 pt-2 font-sans">
      <div className="hidden md:block mb-6">
        <ProgressPill value={80} />
      </div>

      <div className="flex flex-col items-center mt-4 md:mt-8 mb-8 md:mb-0">
        <div className="w-full max-w-3xl rounded-3xl bg-white shadow-sm border border-gray-100 px-2 py-4 md:px-8 md:py-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 pointer-events-none" />
          <div className="relative z-10 w-full">
            <BookingModeSelection onContinue={proceedToSummary} />
          </div>
        </div>
      </div>

      <div className="flex justify-start mt-8 px-2 md:px-0">
        <button
          onClick={() => {
            setBookingState({ selectedSlot: null, bookingStep: "schedule" })
          }}
          className="group px-8 h-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white text-gray-900 text-sm font-semibold hover:border-pink-500 hover:text-pink-600 transition-all duration-300 shadow-sm hover:shadow-md active:scale-95"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          {t("back")}
        </button>
      </div>

      {userModalOpen && (
        <UserModal setUserModalOpen={setUserModalOpen} setAppointerUser={setLocalAppointerUser} setSummaryOpen={setSummaryOpen} />
      )}

      {summaryOpen && (
        <BookingSummaryModal
          booking={booking as any}
          appointerUser={localAppointerUser}
          bookingTab={bookingType}
          setSummaryOpen={setSummaryOpen}
          mode={mode}
          appointmentId={appointmentId ?? undefined}
        />
      )}
    </div>
  )
}

function WizardInner() {
  useDerivedWizardStep()

  const { bookingStep, selectedBranch, selectedService, mode } = useBookingStore()

  if (bookingStep === "branch" || !selectedBranch) return <BranchStep />
  if (bookingStep === "service" || !selectedService) return <ServiceStep />
  if (bookingStep === "schedule") return <SchedulePage mode={mode} />
  return <ModeStep />
}

export default function BookingWizardPage() {
  return (
    <Suspense fallback={<OrganizationSkeleton />}>
      <WizardInner />
    </Suspense>
  )
}

